import { Appearance, LoadActionsSuccess, loadStripe, Stripe, StripeAddressElement, StripeAddressElementChangeEvent, StripeCheckout, StripeElements, StripePaymentElement, StripePaymentElementChangeEvent } from '@stripe/stripe-js';

import BaseController from "@ngenic/controllers/controller-base";
import controllerRegistry from "@ngenic/controllers/controller-register";

interface ShippingContact {
    address: {
        line1: string;
        line2: string | null;
        city: string;
        postal_code: string;
        country: string;
        state: string | null;
    };
    name: string;
    phone?: string;
}

interface FormValidationState {
    email: boolean;
    acceptTermsOfService: boolean;
    shippingAddress: boolean;
    paymentMethod: boolean;
}

class CheckoutController extends BaseController {
    private stripeInstance: Stripe;
    private actions: LoadActionsSuccess;
    private checkoutElementsInstance: StripeElements;
    private paymentElement: StripePaymentElement;
    private addressElement: StripeAddressElement;

    private checkoutForm: HTMLFormElement;
    private submitButton: HTMLButtonElement;
    private installationHelpInput: HTMLInputElement;
    private disabledButton: HTMLButtonElement;
    private loadErrorMessage: HTMLElement;
    private errorMessageElement: HTMLElement;

    private userLang: any;

    private formValidationState: FormValidationState = {
        email: false,
        acceptTermsOfService: false,
        shippingAddress: false,
        paymentMethod: false
    };
    private checkoutError: boolean = false;
    private shippingPersistenceError: boolean = false;
    private isSubmitting: boolean = false;
    private formHasInvalidBackendState: boolean = false;
    private backendValidationPending = false;
    private backendValidationSequence = 0;
    private backendValidationRequests = new WeakMap<object, number>();
    private selectedPaymentType: string = 'card';
    private lastAcceptedShippingAddress: string | null = null;
    private nextShippingAddressKey = 0;
    private addressChangeSequence = 0;
    private pendingShippingSync: Promise<void> = Promise.resolve();
    private shippingSyncSequence = 0;
    private shippingChangeSequence = 0;
    private lifecycleSequence = 0;
    private isDestroyed = false;

    private static readonly SHIPPING_ADDRESS_KEY = '__currentShippingAddress';
    private static readonly FORM_DATA_KEY = '__checkoutFormData';
    private static readonly DEFAULT_COUNTRY = 'SE';

    private selectedCountry: string = CheckoutController.DEFAULT_COUNTRY;
    private lastRequestedCountry: string | null = null;

    constructor(element, data) {
        super(element, data);
    }

    async init(): Promise<void> {
        this.isDestroyed = false;
        const lifecycleSequence = ++this.lifecycleSequence;
        this.checkoutForm = this.element.querySelector("#checkoutForm");
        this.submitButton = this.checkoutForm?.querySelector("button[type='submit']") as HTMLButtonElement;
        this.installationHelpInput = this.checkoutForm?.querySelector("#installationHelp") as HTMLInputElement;
        this.disabledButton = this.checkoutForm?.querySelector("button[disabled].btn-disabled-shadowed, button[disabled].btn-disabled") as HTMLButtonElement;
        this.loadErrorMessage = this.element.querySelector("#stripeLoadError");
        this.errorMessageElement = this.element.querySelector("#stripeError");

        if (!this.checkoutForm)
            return;

        this.updateButtonDisabledState();

        this.setupShippingCostHandling();

        this.stripeInstance = await this.initializeStripe();
        if (!this.isCurrentLifecycle(lifecycleSequence)) return;

        const checkout = this.initializeCheckout();
        const loadActionsResult = await checkout.loadActions();
        if (!this.isCurrentLifecycle(lifecycleSequence)) return;

        if (loadActionsResult.type !== "success") {
            this.handleStripeLoadError();

            return;
        }

        this.actions = loadActionsResult.actions;

        this.mountPaymentElement(checkout);
        this.mountShippingAddressElement(checkout);
        this.attachFormEventHandlers();
        this.restoreFormData();
    }

    private async initializeStripe(): Promise<Stripe> {
        var htmlElement = up.element.get('html');
        this.userLang = htmlElement.getAttribute('lang') || 'sv';

        const stripe = await loadStripe(window["__StripeConfig"].publishableKey, {
            locale: this.userLang
        });

        const self = this;
        const originalElements = stripe.elements.bind(stripe);
        Object.defineProperty(stripe, 'elements', {
            value: function (...args) {
                const instance = originalElements(...args);
                self.checkoutElementsInstance = instance;
                return instance;
            },
            writable: true,
            configurable: true
        });

        return stripe;
    }

    private initializeCheckout() {
        return this.stripeInstance.initCheckout({
            clientSecret: this.data['checkoutSession']['clientSecret'],

            elementsOptions: {
                fonts: [
                    { cssSrc: 'https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,100..900&display=swap' },
                ],
                loader: "always",
                appearance: this.getStripeAppearance()
            }
        });
    }

    private getStripeAppearance(): Appearance {
        return {
            labels: "above", 
            variables: {
                fontFamily: "'Inter', sans-serif",
                colorPrimary: '#333333',
                colorText: '#333333',
                colorTextPlaceholder: '#e3e3e3',
                borderRadius: '12px',
            },
            rules: {
                '.p-Field': {
                    marginBottom: '0.5rem'
                },
                '.Label': {
                    marginBottom: '0.4rem'
                },
                '.Input': {
                    padding: '18px',
                    borderColor: '#b3b3b3'
                },
                '.Input:focus': {
                    borderColor: '#ffe4b0',
                    outline: '0',
                    boxShadow: '0 0 0 0.1875rem rgba(255, 202, 97, 0.25)',
                },
                '.Input--invalid': {
                    color: 'var(--colorDanger)',
                    borderColor: 'var(--colorDanger)',
                    borderWidth: '2px',
                    boxShadow: '',
                },
                '.Input--invalid:focus': {
                    color: 'var(--colorDanger)',
                    borderColor: 'var(--colorDanger)',
                    boxShadow: 'rgba(223, 27, 65, 0.25) 0px 0px 0px 3px',
                }
            }
        };
    }

    private mountPaymentElement(checkout: StripeCheckout): void {
        this.paymentElement = checkout.createPaymentElement({
            layout: {
                type: 'accordion',
                defaultCollapsed: false,
                spacedAccordionItems: false,
            }
        });
        this.paymentElement.on('change', this.onPaymentChange.bind(this))
        this.paymentElement.mount('#s_paymentElement');
    }

    private mountShippingAddressElement(checkout: StripeCheckout): void {
        const savedShippingAddress = this.loadSavedShippingAddress();

        this.addressElement = this.checkoutElementsInstance.create("address", {
            mode: 'shipping',
            allowedCountries: ["SE", "NO", "DK", "FI", "DE", "NL"],
            fields: {
                phone: 'always'
            },
            display: {
                name: 'split'
            },
            validation: {
                phone: { required: 'always' }
            },
            contacts: savedShippingAddress ? [{
                ...savedShippingAddress,
                address: {
                    ...savedShippingAddress.address,
                    line2: savedShippingAddress.address.line2 ?? undefined
                }
            }] : [],
            defaultValues: {
                address: { country: CheckoutController.DEFAULT_COUNTRY }
            }
        });
        this.addressElement.on('change', this.onAddressChange.bind(this));
        this.addressElement.mount('#s_shippingElement');
    }

    private setupShippingCostHandling() {
        up.compiler('[checkout-shipping-retry]', (element: HTMLElement) => {
            return up.on(element, 'click', () => {
                this.onShippingChange(this.lastRequestedCountry ?? this.selectedCountry);
            })
        });
    }

    private attachFormEventHandlers(): void {
        this.sink = up.on(this.checkoutForm, 'input', 'input', this.onValidationInput.bind(this));
        this.sink = up.on(this.checkoutForm, 'change', 'input', this.onInputChange.bind(this));
        this.sink = up.on(this.checkoutForm, 'submit', this.onSubmit.bind(this));
        this.sink = up.on(this.installationHelpInput, 'change', this.onInstallationCheckBoxChanged.bind(this));

        this.sink = up.on('up:request:loaded', this.handleRequestLoaded.bind(this));
        this.sink = up.on('up:request:load', (event) => {
            if (event.origin !== this.checkoutForm || !event.request.headers['X-Up-Validate']) return;
            this.backendValidationPending = true;
            this.backendValidationRequests.set(event.request, this.backendValidationSequence);
        });
    }

    private onValidationInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.id !== 'email' && input.id !== 'acceptTermsOfService') return;

        this.backendValidationSequence++;
        this.backendValidationPending = true;
    }

    private handleStripeLoadError() {
        this.checkoutForm.classList.add("visually-hidden");
        this.loadErrorMessage.classList.remove("visually-hidden");

        const retryButton = this.loadErrorMessage.querySelector("button");
        var unbind = up.on(retryButton, 'click', () => {
            this.checkoutForm.classList.remove("visually-hidden");
            this.loadErrorMessage.classList.add("visually-hidden");

            unbind();
            this.init();
        });
    }

    private handleRequestLoaded(event, _, __) {
        if (event.origin !== this.checkoutForm)
            return;

        const { request, response } = event;

        if (!request.headers['X-Up-Validate'] || request.headers['X-Up-Validate'] === '')
            return;

        if (this.backendValidationRequests.get(request) !== this.backendValidationSequence) return;
        const email = this.checkoutForm.querySelector<HTMLInputElement>('#email');
        const terms = this.checkoutForm.querySelector<HTMLInputElement>('#acceptTermsOfService');
        if (request.params.get(email.name) !== email.value
            || (request.params.get(terms.name) === 'true') !== terms.checked) return;

        this.backendValidationPending = false;
        this.formHasInvalidBackendState = response.status < 200 || response.status >= 300;
        this.updateButtonDisabledState();
    }

    private onInputChange(event: Event): void {
        const input = event.target as HTMLElement;

        if (input instanceof HTMLInputElement) {
            if (!input.id) {
                return; // Skip inputs without IDs
            }

            this.formValidationState[input.id] = input.validity.valid;
            this.updateButtonDisabledState();

            if (!input.validity.valid)
                return;

            const formData = this.loadFormData();

            if (input.type === 'checkbox') {
                formData[input.id] = input.checked;
            } else if (input.type === 'radio') {
                formData[input.name] = input.value;
            } else {
                // Text, email, tel, etc.
                formData[input.id] = input.value;
            }

            this.saveFormData(formData);
        }
    }

    private onInstallationCheckBoxChanged(event: Event): void {
        const input = event.target as HTMLInputElement;
        const installationHelpValue = input?.checked ? "true" : "false";

        if (this.data && this.data['checkoutSession'] && this.data['checkoutSession']['id']) {
            up.request(
                `${this.urlBase}/checkout/index?handler=UpdateCheckoutSession`,
                {
                    method: 'POST',
                    contentType: 'application/json; charset=utf-8',
                    payload: JSON.stringify({
                        checkoutSessionId: this.data['checkoutSession']['id'],
                        metadata: {
                            installation_help: installationHelpValue
                        }
                    })
                }
            );
        }
    }

    private restoreFormData(): void {
        const formData = this.loadFormData();

        let fieldDirty;
        for (const [key, value] of Object.entries(formData)) {
            fieldDirty = false;
            const input = this.checkoutForm.querySelector(`#${key}`) as HTMLInputElement;

            if (!input) {
                continue;
            }

            if (input.type === 'checkbox') {
                input.checked = Boolean(value);
                fieldDirty = true;
            } else if (input.type === 'radio') {
                const radioInput = this.checkoutForm.querySelector(
                    `input[name="${key}"][value="${value}"]`
                ) as HTMLInputElement;
                if (radioInput) {
                    radioInput.checked = true;
                    fieldDirty = true;
                }
            } else {
                input.value = String(value);
                fieldDirty = true;
            }

            if (fieldDirty) {
                if (input.id === 'email' || input.id === 'acceptTermsOfService') {
                    this.backendValidationPending = true;
                }
                up.validate(input);
            }

            if (Object.keys(this.formValidationState).includes(input.id)) {
                this.formValidationState[input.id] = input.validity.valid;
                this.updateButtonDisabledState();
            }
        }
    }

    private updateButtonDisabledState() {
        if (this.checkoutError) {
            this.disabledButton.classList.remove('visually-hidden');
            this.disabledButton.classList.add('w-100');
            this.submitButton.classList.add('visually-hidden');
        } else {
            this.disabledButton.classList.add('isually-hidden');
            this.disabledButton.classList.remove('w-100');
            this.submitButton.classList.remove('visually-hidden');
        }

        this.submitButton.disabled = this.isSubmitting || !Object.values(this.formValidationState).every(v => v) || this.formHasInvalidBackendState || this.checkoutError || this.shippingPersistenceError;
    }

    private async onAddressChange(event: StripeAddressElementChangeEvent): Promise<void> {
        if (!this.actions) return;
        const lifecycleSequence = this.lifecycleSequence;
        const addressChangeSequence = ++this.addressChangeSequence;
        const isCurrent = () =>
            this.isCurrentLifecycle(lifecycleSequence) &&
            addressChangeSequence === this.addressChangeSequence;

        const validName = this.hasValidShippingContact(event.value);
        this.formValidationState.shippingAddress = event.complete && validName;
        this.updateButtonDisabledState();

        const country = event.value.address.country;
        let summaryUpdated = true;
        if (country !== this.selectedCountry || this.checkoutError) {
            summaryUpdated = (await this.onShippingChange(country)) !== null;
            if (!isCurrent()) return;
        }

        if (event.complete && validName && summaryUpdated) {
            this.saveShippingAddress(event.value);
            const shippingAddress = this.toStripeShippingAddress(event.value);
            const deduplicationKey = JSON.stringify(shippingAddress);
            const sync = await this.queueShippingSync(event.value);
            if (!sync.ok) return;
            if (!isCurrent()) return;
            if (this.lastAcceptedShippingAddress === deduplicationKey) return;

            this.lastAcceptedShippingAddress = deduplicationKey;
            window.dispatchEvent(new CustomEvent('ngenic:checkout-shipping-accepted', {
                detail: {
                    checkoutSessionId: this.data['checkoutSession']['id'],
                    addressKey: `shipping-address-${++this.nextShippingAddressKey}`
                }
            }));
        }
    }

    private async onShippingChange(country: string = null): Promise<{ value: number; tax: number; shipping: number } | null> {
        const lifecycleSequence = this.lifecycleSequence;
        const shippingChangeSequence = ++this.shippingChangeSequence;
        const isCurrent = () =>
            this.isCurrentLifecycle(lifecycleSequence) &&
            shippingChangeSequence === this.shippingChangeSequence;
        if (country) this.lastRequestedCountry = country;
        try {
            const localRenderOptions = {
                target: '.checkout-summary__shipping,.checkout-summary-card__totalsummary',
                failTarget: '.checkout-summary__shipping,.checkout-summary-card__totalsummary',
                url: `${this.urlBase}/checkout/index?handler=UpdateCheckoutSession`,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json; charset=utf-8'
                },
                payload: JSON.stringify({
                    checkoutSessionId: this.data['checkoutSession']['id'],
                    countryCode: country
                }),
                preview: "summary",
            };

            await up.render(localRenderOptions);
            if (!isCurrent()) return null;

            const summary = this.element.querySelector<HTMLElement>('.checkout-summary-card__totalsummary');
            const valueText = summary?.dataset.checkoutSummaryValue;
            const taxText = summary?.dataset.checkoutSummaryTax;
            const shippingText = summary?.dataset.checkoutSummaryShipping;
            if (valueText === undefined || taxText === undefined || shippingText === undefined || !valueText.trim() || !taxText.trim() || !shippingText.trim()) return null;

            const snapshot = {
                value: Number(valueText),
                tax: Number(taxText),
                shipping: Number(shippingText)
            };
            if (!Object.values(snapshot).every(Number.isFinite)) return null;

            if (country) this.selectedCountry = country;
            window.dispatchEvent(new CustomEvent('ngenic:checkout-summary-updated', {
                detail: {
                    checkoutSessionId: this.data['checkoutSession']['id'],
                    ...snapshot
                }
            }));
            this.checkoutError = false;
            return snapshot;
        } catch (error) {
            if (!isCurrent()) return null;
            if (error instanceof up.RenderResult) {
                this.checkoutError = true;
            } else if (error instanceof up.CannotMatch) {
                console.log("Could not find .target in current page or response")
            } else if (error instanceof up.Aborted) {
                console.log("Request to aborted")
            } else if (error instanceof up.Offline) {
                this.checkoutError = true;
            } else {
                this.checkoutError = true;
            }
        } finally {
            if (isCurrent()) {
                this.updateButtonDisabledState();
            }
        }
        return null;
    }

    private onPaymentChange(event: StripePaymentElementChangeEvent) {
        this.formValidationState.paymentMethod = event.complete;
        if (typeof event.value?.type === 'string' && event.value.type.length > 0) {
            this.selectedPaymentType = event.value.type;
        }
        this.updateButtonDisabledState();
    }

    private async onSubmit(event: Event, element: HTMLFormElement, data: any): Promise<void> {
        event.preventDefault();

        if (this.isSubmitting) return;

        if (!this.stripeInstance || !this.actions) {
            // Stripe.js hasn't yet loaded.
            // Make sure to disable form submission until Stripe.js has loaded.
            return;
        }

        if (!this.checkoutForm.checkValidity()) {
            // Just return when form is invalid to prevent confirming payment without required
            // information. Actual form validation is performed elsewhere.
            return;
        }

        this.toggleLoadingSubmit(true);

        try {
            let shippingValue: Awaited<ReturnType<StripeAddressElement['getValue']>>;
            let addressChangeSequence: number;
            const isAddressCurrent = () => addressChangeSequence === this.addressChangeSequence;

            // A valid address may still have syncs in flight when submit is clicked,
            // and the address fields remain editable while we await network calls.
            // The queue preserves write order; this loop retries if an edit or newer
            // sync supersedes our snapshot, so payment uses the latest persisted address.
            while (true) {
                addressChangeSequence = this.addressChangeSequence;
                shippingValue = await this.addressElement.getValue();
                const shippingKey = JSON.stringify(shippingValue.value);
                if (!isAddressCurrent()) continue;

                const validShipping = shippingValue.complete && this.hasValidShippingContact(shippingValue.value);
                this.formValidationState.shippingAddress = validShipping;
                this.updateButtonDisabledState();
                if (!validShipping) return;

                if (this.checkoutError || this.selectedCountry !== shippingValue.value.address.country) {
                    const summary = await this.onShippingChange(shippingValue.value.address.country);
                    if (!isAddressCurrent()) continue;
                    if (!summary) return;
                }

                // Serialized Stripe update + backend persistence, tracked by generation.
                const sync = await this.queueShippingSync(shippingValue.value);
                const isLatestShippingSync = () => sync.sequence === this.shippingSyncSequence;

                const latestValue = await this.addressElement.getValue();
                if (!isAddressCurrent() || JSON.stringify(latestValue.value) !== shippingKey) continue;
                // A newer sync (e.g. from a concurrent edit) owns the error state; re-sync instead of trusting this one.
                if (!isLatestShippingSync()) continue;
                if (!sync.ok || this.checkoutError || this.selectedCountry !== latestValue.value.address.country) return;
                shippingValue = latestValue;
                break;
            }

            if (!isAddressCurrent()
                || !this.checkoutForm.checkValidity()
                || !this.checkoutForm.querySelector<HTMLInputElement>('#acceptTermsOfService').checked
                || !Object.values(this.formValidationState).every(v => v)
                || this.formHasInvalidBackendState
                || this.backendValidationPending
                || this.checkoutError
                || this.shippingPersistenceError
                || this.selectedCountry !== shippingValue.value.address.country
                || !shippingValue.complete
                || !this.hasValidShippingContact(shippingValue.value)) {
                return;
            }

            this.errorMessageElement.classList.add('visually-hidden');

            const emailElement: HTMLInputElement = this.checkoutForm.querySelector("#email");

            window.dispatchEvent(new CustomEvent('ngenic:checkout-payment-submitted', {
                detail: {
                    checkoutSessionId: this.data['checkoutSession']['id'],
                    paymentType: this.selectedPaymentType
                }
            }));

            const confirmResult = await this.actions.confirm({
                email: emailElement.value
            });

            if (confirmResult.type == "error") {
                const { error } = confirmResult;

                this.errorMessageElement.querySelector('.message').textContent = error.message;
                this.errorMessageElement.classList.remove('visually-hidden');
            }
        } catch (error) {
            console.error('Payment submission error:', error);
            this.errorMessageElement.querySelector('.message').textContent = error;
            this.errorMessageElement.classList.remove('visually-hidden');
        } finally {
            this.toggleLoadingSubmit(false);
        }
    }

    private hasValidShippingContact(value: { firstName?: string; lastName?: string; phone?: string }): boolean {
        return !!value.firstName?.trim() && !!value.lastName?.trim() && !!value.phone?.trim();
    }

    private toStripeShippingAddress(value: StripeAddressElementChangeEvent['value']) {
        return {
            name: value.name,
            phone: value.phone ?? '',
            address: {
                line1: value.address.line1,
                line2: value.address.line2,
                city: value.address.city,
                postal_code: value.address.postal_code,
                country: value.address.country,
                state: value.address.state
            }
        };
    }

    private queueShippingSync(value: StripeAddressElementChangeEvent['value']): Promise<{ ok: boolean; sequence: number }> {
        const sequence = ++this.shippingSyncSequence;
        const isCurrent = () => sequence === this.shippingSyncSequence && !this.isDestroyed;
        // StripeCheckoutContact accepts only { name, address }; drop phone and split-name keys.
        const stripeAddress = { name: value.name, address: value.address };
        const payload = JSON.stringify({
            checkoutSessionId: this.data['checkoutSession']['id'],
            shippingAddress: {
                name: value.name,
                firstName: value.firstName,
                lastName: value.lastName,
                address: {
                    line1: value.address.line1,
                    line2: value.address.line2,
                    city: value.address.city,
                    state: value.address.state,
                    postalCode: value.address.postal_code,
                    country: value.address.country
                },
                phone: value.phone
            },
            metadata: {}
        });

        const task = this.pendingShippingSync.then(async () => {
            if (!this.actions) return false;
            const result = await this.actions.updateShippingAddress(stripeAddress);
            if (result.type !== 'success') return false;
            await up.request(
                `${this.urlBase}/checkout/index?handler=UpdateCheckoutSession`,
                {
                    method: 'POST',
                    contentType: 'application/json; charset=utf-8',
                    payload
                }
            );
            return true;
        }).catch(() => false).then(ok => {
            // Only the newest sync may set/clear the error; older results must not overwrite it.
            if (isCurrent()) {
                this.shippingPersistenceError = !ok;
                this.updateButtonDisabledState();
            }
            return { ok, sequence };
        });
        this.pendingShippingSync = task.then(() => undefined);
        return task;
    }

    private toggleLoadingSubmit(isLoading: boolean) {
        this.isSubmitting = isLoading;
        const text = this.submitButton.querySelector('span:not(.spinner-border)');
        const spinner = this.submitButton.querySelector('span.spinner-border');

        this.submitButton.disabled = true;

        if (isLoading) {
            text.classList.add('visually-hidden');
            spinner.classList.remove('visually-hidden');
        } else {
            text.classList.remove('visually-hidden');
            spinner.classList.add('visually-hidden');
            this.updateButtonDisabledState();
        }
    }

    override async destroy(): Promise<void> {
        this.isDestroyed = true;
        this.lifecycleSequence++;
        this.shippingChangeSequence++;
        super.destroy();

        this.stripeInstance = null;
        this.actions = null;
        this.checkoutElementsInstance = null;

        if (this.paymentElement != null) {
            this.paymentElement.unmount();
            this.paymentElement.destroy();
        }

        if (this.addressElement != null) {
            this.addressElement.unmount();
            this.addressElement.destroy();
        }

        // TODO: Look into perhaps doing this on history change instead.
        // Presumably it would be better to detect a navigation away from
        // the Checkout view into another view on the site, not externally,
        // since Stripe.js is redirecting users away from this view for
        // payment verification.
        try {
            await up.request(
                `${this.urlBase}/checkout/index?handler=CancelCheckoutSession`,
                {
                    method: 'POST',
                    contentType: 'application/json; charset=utf-8',
                    payload: JSON.stringify({
                        checkoutSessionId: this.data['checkoutSession']['id']
                    })
                }
            );
        } catch (error) {
            console.warn('Failed to cancel checkout session:', error);
        }
    }

    private loadFormData(): Record<string, any> {
        const sessionValue = sessionStorage.getItem(CheckoutController.FORM_DATA_KEY);

        if (!sessionValue) {
            return {};
        }

        try {
            return JSON.parse(sessionValue);
        } catch (error) {
            console.warn('Failed to parse saved form data:', error);
            return {};
        }
    }

    private saveFormData(formData: Record<string, any>): void {
        try {
            sessionStorage.setItem(
                CheckoutController.FORM_DATA_KEY,
                JSON.stringify(formData)
            );
        } catch (error) {
            console.warn('Failed to save form data:', error);
        }
    }

    private loadSavedShippingAddress(): ShippingContact | null {
        const sessionValue = sessionStorage.getItem(CheckoutController.SHIPPING_ADDRESS_KEY);

        if (!sessionValue) {
            return null;
        }

        try {
            const shippingAddress = JSON.parse(sessionValue) as ShippingContact;

            if (shippingAddress?.address) {
                return shippingAddress;
            }

            return null;
        } catch (error) {
            console.warn('Failed to parse saved shipping address:', error);
            return null;
        }
    }

    private saveShippingAddress(shippingContact: ShippingContact): void {
        try {
            sessionStorage.setItem(
                CheckoutController.SHIPPING_ADDRESS_KEY,
                JSON.stringify(shippingContact)
            );
        } catch (error) {
            console.warn('Failed to save shipping address:', error);
        }
    }

    private isCurrentLifecycle(sequence: number): boolean {
        return !this.isDestroyed && sequence === this.lifecycleSequence;
    }
}

controllerRegistry.register('CheckoutController', CheckoutController);
